import EmailCampaigns from 'components/EmailMarketing/EmailCampaigns/EmailCampaigns';
import EmailSenderHost from 'components/EmailMarketing/EmailSender/EmailSenderHost';
import EmailTemplates from 'components/EmailMarketing/EmailTemplates/EmailTemplates';
import EmailMarketingTabs, {
    emailCampaignsTabId,
    emailSenderTabId,
    emailTemplatesTabId
} from 'components/EmailMarketing/UI/EmailMarketingTabs';
import { FunctionComponent, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { MarketingEmailsPath } from 'routes/Paths';
import type { EmailTemplatesView } from './EmailTemplates/EmailTemplates';
import styles from './EmailMarketingComponent.module.scss';

const tabIdFromQueryValue = (value: string | null): string => {
    if (value === 'templates') return emailTemplatesTabId;
    if (value === 'sender') return emailSenderTabId;
    return emailCampaignsTabId;
};

const queryValueFromTabId = (tabId: string): string => {
    if (tabId === emailTemplatesTabId) return 'templates';
    if (tabId === emailSenderTabId) return 'sender';
    return 'campaigns';
};

const EmailMarketingComponent: FunctionComponent = () => {
    const history = useHistory();
    const location = useLocation();

    // `?editCampaign=ID` is a deep-link into the campaign wizard with a specific
    // draft. It implies the campaigns tab regardless of `?tab=`, so when both
    // are missing/inconsistent we land on campaigns.
    const searchParams = new URLSearchParams(location.search);
    const hasEditCampaign = searchParams.has('editCampaign');
    const initialTabId = hasEditCampaign ? emailCampaignsTabId : tabIdFromQueryValue(searchParams.get('tab'));

    const [selectedTabId, setSelectedTabId] = useState(initialTabId);
    const [emailTemplatesView, setEmailTemplatesView] = useState<EmailTemplatesView>('list');

    const handleSelectTab = (tabId: string) => {
        setSelectedTabId(tabId);
        history.replace(`${MarketingEmailsPath}?tab=${queryValueFromTabId(tabId)}`);

        if (tabId === emailTemplatesTabId) {
            setEmailTemplatesView('list');
        }
    };

    const handleStartWithTemplate = () => {
        setSelectedTabId(emailTemplatesTabId);
        history.replace(`${MarketingEmailsPath}?tab=templates`);
        setEmailTemplatesView('gallery');
    };

    const handleCloseGallery = () => {
        setEmailTemplatesView('list');
    };

    return (
        <div className={styles.root}>
            <EmailMarketingTabs
                selectedTabId={selectedTabId}
                onSelect={handleSelectTab}
                className={styles.emailMarketingTabs}
            />

            <div className={styles.content}>
                {selectedTabId === emailCampaignsTabId && <EmailCampaigns />}
                {selectedTabId === emailTemplatesTabId && (
                    <EmailTemplates
                        view={emailTemplatesView}
                        onStartWithTemplate={handleStartWithTemplate}
                        onCloseGallery={handleCloseGallery}
                    />
                )}
                {selectedTabId === emailSenderTabId && <EmailSenderHost />}
            </div>
        </div>
    );
};

export default EmailMarketingComponent;
